import {COMMON_BUILDING_TOKENS, TERRAIN_TOKENS} from '../../constants/tokens';
import {ZERO_FIELD_INSETS} from '../visuals/fieldInsets';

// Кадры камеры поля матча ставятся в центр СВОБОДНОЙ области — окна за
// вычетом краев под шапкой, футером и панелями (utils/visuals/fieldInsets), а
// не в центр окна: иначе цель уходила бы под интерфейс.
//
// Единицы: отступы — в CSS-пикселях, камера живет в буферных пикселях канвы
// (CSS × DPR), а мир от буфера отличается зумом камеры

/**
 * Насколько центр свободной области смещен от центра кадра — в единицах мира
 * @param {{top: number, right: number, bottom: number, left: number}} insets — CSS-пиксели
 * @param {number} dpr — буферных пикселей в CSS-пикселе
 * @param {number} zoom — зум камеры
 * @returns {{x: number, y: number}}
 */
export const getFreeAreaWorldOffset = (insets = ZERO_FIELD_INSETS, dpr = 1, zoom = 1) => {
  const worldPerCssPixel = dpr / (zoom || 1);
  return {
    x: (((insets.left || 0) - (insets.right || 0)) / 2) * worldPerCssPixel,
    y: (((insets.top || 0) - (insets.bottom || 0)) / 2) * worldPerCssPixel,
  };
};

/**
 * Насколько расширить границы прокрутки камеры, чтобы крайние ячейки можно
 * было вывести из-под интерфейса в свободную область при самом дальнем зуме
 * @param {{top: number, right: number, bottom: number, left: number}} insets
 * @param {number} dpr
 * @param {number} minZoom
 * @returns {number} единицы мира
 */
export const getFreeAreaBoundsPadding = (insets = ZERO_FIELD_INSETS, dpr = 1, minZoom = 1) => {
  const maxInset = Math.max(insets.top || 0, insets.right || 0, insets.bottom || 0, insets.left || 0);
  return maxInset * (dpr / (minZoom || 1));
};

/**
 * Размер свободной области в единицах мира: кадр за вычетом краев интерфейса
 * @param {{width: number, height: number}} viewport — кадр камеры в буферных пикселях
 * @param {{top: number, right: number, bottom: number, left: number}} insets — CSS-пиксели
 * @param {number} dpr
 * @param {number} zoom
 * @returns {{width: number, height: number}}
 */
export const getFreeAreaWorldSize = (viewport, insets = ZERO_FIELD_INSETS, dpr = 1, zoom = 1) => {
  const worldPerBufferPixel = 1 / (zoom || 1);
  const worldPerCssPixel = dpr * worldPerBufferPixel;
  return {
    width: viewport.width * worldPerBufferPixel - ((insets.left || 0) + (insets.right || 0)) * worldPerCssPixel,
    height: viewport.height * worldPerBufferPixel - ((insets.top || 0) + (insets.bottom || 0)) * worldPerCssPixel,
  };
};

/**
 * Центр свободной области для подъезда к точке у края поля (автовыбор отряда,
 * change unit-auto-selection): кадр держит как можно больше игровой части
 * поля, а не пустую подложку за ней. По каждой оси: если игровая часть (с
 * запасом margin) шире свободной области — центр сдвигается от точки ровно
 * настолько, чтобы край области не вышел за край поля; если уже — центр встает
 * на середину поля. Точка внутри поля при этом всегда остается в свободной
 * области: сдвиг не больше половины ее размера.
 * @param {{x: number, y: number}} point — куда хотелось бы поставить центр
 * @param {{centerX: number, centerY: number, width: number, height: number}|null} area — игровая часть поля
 * @param {{width: number, height: number}} frame — размер свободной области в мире
 * @param {number} margin — запас за краем поля, единицы мира
 * @returns {{x: number, y: number}}
 */
export const clampFreeAreaCenterToPlayArea = (point, area, frame, margin = 0) => {
  if (!area) {
    return point;
  }
  const clampAxis = (value, areaCenter, areaSize, frameSize) => {
    const halfSpan = areaSize / 2 + margin;
    const halfFrame = frameSize / 2;
    if (halfSpan <= halfFrame) {
      return areaCenter;
    }
    return Math.min(Math.max(value, areaCenter - halfSpan + halfFrame), areaCenter + halfSpan - halfFrame);
  };
  return {
    x: clampAxis(point.x, area.centerX, area.width, frame.width),
    y: clampAxis(point.y, area.centerY, area.height, frame.height),
  };
};

/**
 * Отступ границ прокрутки камеры от краев сетки — по каждой оси свой.
 *
 * Не меньше обычного (basePadding), но и не меньше того, что нужно кадру
 * самого дальнего зума: он должен целиком помещаться в границы с запасом на
 * сдвиг к центру свободной области (freeAreaPadding). Кадр шире границ Phaser
 * не центрирует, а прижимает к левому (верхнему) краю границ — на большом
 * окне с плотным экраном поле при полном отдалении съезжало, будто камера
 * уперлась в стену слева. Чернота за подложкой не рисуется, поэтому запас
 * отрисовке ничего не стоит
 *
 * @param {Object} params
 * @param {number} params.gridWidth - ширина сетки, единицы мира
 * @param {number} params.gridHeight - высота сетки
 * @param {number} params.cameraWidth - кадр в буферных пикселях канвы
 * @param {number} params.cameraHeight
 * @param {number} params.minZoom - самый дальний зум
 * @param {number} params.basePadding - обычный отступ
 * @param {number} params.freeAreaPadding - запас на сдвиг к свободной области
 *   (getFreeAreaBoundsPadding)
 * @returns {{x: number, y: number}} единицы мира
 */
export const calcCameraBoundsPadding = ({
  gridWidth,
  gridHeight,
  cameraWidth,
  cameraHeight,
  minZoom,
  basePadding,
  freeAreaPadding = 0,
}) => {
  const zoom = minZoom || 1;
  return {
    x: Math.max(basePadding, (cameraWidth / zoom - gridWidth) / 2 + freeAreaPadding),
    y: Math.max(basePadding, (cameraHeight / zoom - gridHeight) / 2 + freeAreaPadding),
  };
};

const calcPointsBounds = (hexList) => {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  hexList.forEach((hex) => {
    (hex.points || []).forEach((point) => {
      if (point.x < minX) minX = point.x;
      if (point.x > maxX) maxX = point.x;
      if (point.y < minY) minY = point.y;
      if (point.y > maxY) maxY = point.y;
    });
  });
  if (!Number.isFinite(minX)) {
    return null;
  }
  return {
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2,
    width: maxX - minX,
    height: maxY - minY,
  };
};

// Дороги в кадр не тянем: они уходят длинными хвостами и уводили бы центр от
// городов. Море и горы — не застройка
const NOT_OCCUPYING_TOKENS = new Set([
  COMMON_BUILDING_TOKENS.ROAD,
  TERRAIN_TOKENS.SEA,
  TERRAIN_TOKENS.MOUNTAIN,
]);

/**
 * Прямоугольник занятых ячеек: кварталы, строения, ключевые точки и флаги
 * (жетон на ячейке, кроме дорог, моря и гор) плюс ячейки с отрядами
 * @param {Object<string, string>} hexTokens — hexKey → жетон (selectHexes)
 * @param {Array<{hexKey: string}>} units
 * @param {Object<string, {points: Array<{x: number, y: number}>}>} rendererHexes — отрисованные ячейки
 * @returns {{centerX: number, centerY: number, width: number, height: number}|null} null — занятых нет
 */
export const calcOccupiedCellsBounds = (hexTokens, units, rendererHexes) => {
  const keys = new Set();
  Object.entries(hexTokens || {}).forEach(([hexKey, token]) => {
    if (token && !NOT_OCCUPYING_TOKENS.has(token)) {
      keys.add(hexKey);
    }
  });
  (units || []).forEach((unit) => {
    if (unit?.hexKey) {
      keys.add(unit.hexKey);
    }
  });
  const hexList = [...keys].map((hexKey) => rendererHexes?.[hexKey]).filter(Boolean);
  return calcPointsBounds(hexList);
};
